import React, { useState, useEffect } from 'react';
import { Note, CreateNoteRequest, NotesFilterStruct } from '../../models/Note';
import NoteSearch from './NoteSearch';
import NoteForm from './NoteForm';
import NoteList from './NoteList';
import { useNotesApi, useNotesSafeRequests } from '../../api/useNotesApi';
import NotesFilter from './NotesFilter';
import './ArtworkNotes.css';
import { useDebounce } from '../../hooks/useDebounce';
import { Typography, Button, Divider, Box, Paper } from "@mui/material";
import { useUser } from '../../components/UserContext';

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../styles/mui_theme";
interface ArtworkNotesProps {
    artworkId: number;
    dialogOpen: boolean;
    setOpenDialog: (newDialogState: boolean) => void;
}

const ArtworkNotes: React.FC<ArtworkNotesProps> = ({ artworkId, dialogOpen, setOpenDialog }) => {
    const { api, activeClientId, isAuthenticated } = useNotesApi();
    const {
        getNotesReq,
        createNoteReq,
        updateNoteReq,
        deleteNoteReq,
        getTagsReq
    } = useNotesSafeRequests(api);

    // State variables
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [refreshTagsTrigger, setRefreshTagsTrigger] = useState<number>(0);
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    const { isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    // Separate search from other filters for better debouncing
    const [searchInput, setSearchInput] = useState<string>('');
    const [filters, setFilters] = useState<Omit<NotesFilterStruct, 'search'>>({
        noteTypeId: 0,
        tags: [],
        priority: '' // Add priority
    });

    const loadPopularTags = async (): Promise<void> => {
        try {
            const tags = await getTagsReq.run({
                method: "GET",
                url: `/notes/tags`,
                params: { artworkId, count: 50 } // Get more tags for suggestions
            });
            setAvailableTags(tags || []);
        } catch (error) {
            setAvailableTags([]);
        }
    };

    useEffect(() => {
        loadPopularTags();
    }, [artworkId, refreshTagsTrigger]);

    // Debounce the search input (600ms delay)
    const debouncedSearch = useDebounce(searchInput, 600);

    // Load notes when artworkId, filters, or debounced search changes
    useEffect(() => {
        if (!isAuthenticated || !activeClientId) return;
        loadNotes();
    }, [artworkId, filters, debouncedSearch, isAuthenticated, activeClientId]);

    const loadNotes = async (): Promise<void> => {
        if (!isAuthenticated || !activeClientId) return;

        setLoading(true);
        setError(null);

        try {
            const queryParams: Record<string, any> = {};

            if (debouncedSearch && debouncedSearch.trim()) {
                queryParams.search = debouncedSearch.trim();
            }

            if (filters.noteTypeId && filters.noteTypeId != 0) {
                queryParams.noteTypeId = filters.noteTypeId;
            }

            if (filters.priority && filters.priority !== '') {
                queryParams.priority = filters.priority; // Add priority to API call
            }

            if (filters.tags && filters.tags.length > 0) {
                queryParams.tags = filters.tags.join(',');
            }

            const result = await getNotesReq.run({
                method: "GET",
                url: `/notes/artwork/${artworkId}`,
                params: queryParams
            });

            setNotes(result || []);
            setEditingNote({} as Note);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load notes');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = () => {
        setEditingNote({} as Note);
        setOpenDialog(true);
    };

    const handleEditNote = (NoteToEdit: Note) => {
        setEditingNote(NoteToEdit);
        setOpenDialog(true);
    }

    const handleSearchChange = (search: string): void => {
        setSearchInput(search);
    };

    const handleFiltersChange = (newFilters: Omit<NotesFilterStruct, 'search'>): void => {
        setFilters(newFilters);
    };

    const handleCreateNote = async (noteData: CreateNoteRequest): Promise<void> => {
        if (!isAuthenticated || !activeClientId) {
            setError('Authentication required');
            return;
        }

        try {
            await createNoteReq.run({
                method: "POST",
                url: '/notes',
                data: { ...noteData, artworkId }
            });

            await loadNotes();
            setOpenDialog(false);
            setEditingNote(null);
            setRefreshTagsTrigger(prev => prev + 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create note');
        }
    };

    const handleUpdateNote = async (noteData: CreateNoteRequest): Promise<void> => {
        if (!editingNote?.id || !isAuthenticated || !activeClientId) return;

        try {
            await updateNoteReq.run({
                method: "PUT",
                url: `/notes/${editingNote.id}`,
                data: noteData
            });

            await loadNotes();
            setOpenDialog(false);
            setEditingNote(null);
            setRefreshTagsTrigger(prev => prev + 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update note');
        }
    };

    const handleDeleteNote = async (noteId: number): Promise<void> => {
        if (!isAuthenticated || !activeClientId) {
            setError('Authentication required');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this note?')) return;

        try {
            await deleteNoteReq.run({
                method: "DELETE",
                url: `/notes/${noteId}`
            });

            await loadNotes();
            setRefreshTagsTrigger(prev => prev + 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete note');
        }
    };

    const handleFormCancel = (): void => {
        setOpenDialog(false);
        setEditingNote(null);
        setRefreshTagsTrigger(prev => prev + 1);
    };

    if (!isAuthenticated || !activeClientId) {
        return (
            <div className="artwork-notes">
                <div className="auth-required">
                    <p>Please sign in to view and manage notes.</p>
                </div>
            </div>
        );
    }

    return (
        <ThemeProvider theme={theme}>
        <Paper elevation={3} square={false} sx={{ p: 1, width: '100%', boxSizing: 'border-box' }}>
        <div className="artwork-notes">
            <div className="section-header">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Notes</Typography>
                            <button className="responsive-button" onClick={handleAddNote} hidden={true}>Add Note</button>
                </Box>
            </div>
            <Divider sx={{ my: 2 }} />
            {error && (
                <div className="error-message">
                    {error}
                    <button
                        onClick={() => setError(null)}
                        className="error-close"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="notes-controls">
                {/*<NoteSearch*/}
                {/*    value={searchInput}*/}
                {/*    onChange={handleSearchChange}*/}
                {/*    disabled={loading}*/}
                {/*/>*/}
                <NotesFilter
                    filters={{ noteTypeId: filters.noteTypeId, tags: filters.tags, priority: filters.priority }} 
                    onChange={handleFiltersChange}
                    artworkId={artworkId}
                    api={api}
                    disabled={loading}
                    refreshTrigger={refreshTagsTrigger} 
                />
            </div>


            <NoteForm
                open={dialogOpen}
                note={editingNote}
                onSubmit={editingNote?.id ? handleUpdateNote : handleCreateNote}
                onCancel={handleFormCancel}
                loading={loading}
                availableTags={availableTags} 
            />
  
            <NoteList
                notes={notes}
                loading={loading}
                        onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                    onSearch={handleSearchChange}
            />
            </div>
            </Paper>
        </ThemeProvider>
    );
};

export default ArtworkNotes;